import {
  type ChangeEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type SubmitHandler,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { HiOutlineColorSwatch } from 'react-icons/hi';
import { ZodType } from 'zod';

import {
  BaseCollapsibleMenu,
  DefaultDescriptionBenefitEducation,
  FeatureFlag,
  FeatureFlagKey,
  FormBase,
  IApiThrowsError,
  type IFormModalProps,
  Logger,
  useFeatureFlag,
  useToast,
} from '@gbs-monorepo-packages/common';
import { ColorPickerInput } from '@gbs-monorepo-packages/common';
import { zodResolver } from '@hookform/resolvers/zod';

import { ColorPickerSelectData } from '../../../../components/ColorPickerSelectData';
import {
  fontUnits as fontUnitsData,
  fonts as fontsGrapes,
} from '../../../../constants/Fonts';
import {
  type IUpdateGlobalStyle,
  globalStyleDefault,
  maxHueRotate,
  maxValueFontSize,
  maxValuePageWidth,
  maxValuePercentagePageWidth,
  minHueRotate,
  minValueFontSize,
  minValuePageWidth,
} from '../../../../constants/GlobalStyles';
import {
  type GlobalStyleSchema,
  type GlobalStyleSchemaWithHue,
  type TemplateColorsSchema,
  globalStyleSchema,
  globalStyleWithHueSchema,
} from '../../../../formSchemas/globalStyleSchema';
import { useCourse } from '../../../../hooks/useCourse';
import { adminApi } from '../../../../services/api';
import {
  type IFontDTO,
  IGlobalStyleDTO,
  getGlobalStyleById,
  swapColors,
} from '../../../../services/courses';
import { ModalSwapColors } from '../ModalSwapColors';
import {
  generateAdditionalColorsToUpdate,
  generateOldNewAdditionalColors,
  generateOldNewTemplateColors,
  generateTemplateColorsToUpdate,
} from '../shared/globalStyleHelpers';
import {
  ColorPickerContainer,
  ColorPreview,
  Container,
  FormModalCustom,
  IconButton,
  InputContentCustom,
  Loading,
  LoadingContainer,
  OverlayDiv,
  OverlayText,
  SelectDataCustom,
  SliderCustom,
  SmallInputTextCustom,
  SmallSelectDataCustom,
  TemplateImagesColorsContainer,
  TemplateImagesColorsSection,
  TemplateImagesColorsTitle,
} from './styles';

export interface IFont extends IFontDTO {
  index: number;
  origin?: string;
  selected?: boolean;
}

type IGlobalStyleModalBaseProps<T extends GlobalStyleSchema> = Partial<
  Omit<IFormModalProps, 'children' | 'onAccept' | 'onOpenChange'>
> & {
  courseId: number;
  loading: boolean;
  onAccept: (data: IUpdateGlobalStyle) => Promise<void>;
  onDecline: () => void;
  open: boolean;
  waitToOpen?: boolean;
  needSave?: boolean;
  schema?: ZodType<T>;
  defaultExtraValues?: Partial<T>;
  extraFields?: ReactNode;
};

const NumberKeyRegex = /[0-9]/;

export const GlobalStyleModalBase = ({
  courseId,
  loading,
  onAccept,
  onDecline,
  open,
  waitToOpen = false,
  needSave = false,
  extraFields,
  schema = globalStyleSchema,
  defaultExtraValues = {} as Partial<GlobalStyleSchema>,
  ...props
}: IGlobalStyleModalBaseProps<GlobalStyleSchema>): JSX.Element | null => {
  const [isSwapColorsOpen, setIsSwapColorsOpen] = useState(false);
  const [isLoadingModalSwapColors, setIsLoadingModalSwapColors] =
    useState(false);
  const isSelectOpen = useRef(new Set());
  const { selectedCourse, updateGlobalStyle } = useCourse();

  const { isEnabled: isHueRotateRemoved } = useFeatureFlag({
    api: adminApi,
    flagKey: FeatureFlagKey.REMOVE_HUEROTATE,
  });

  const globalStyleSchemaModalForm = useForm<GlobalStyleSchema>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      buttonColor:
        selectedCourse?.globalStyle?.buttonColor ??
        globalStyleDefault.buttonColor,
      buttonFontColor:
        selectedCourse?.globalStyle?.buttonFontColor ??
        globalStyleDefault.buttonFontColor,
      headlineFont: selectedCourse?.globalStyle?.headlineFont?.family ?? '',
      headlineFontColor:
        selectedCourse?.globalStyle?.headlineFontColor ??
        globalStyleDefault.headlineFontColor,
      headlineFontSize:
        selectedCourse?.globalStyle?.headlineFontSize ??
        globalStyleDefault.headlineFontSize,
      headlineFontSizeUnit:
        selectedCourse?.globalStyle?.headlineFontSizeUnit ??
        globalStyleDefault.headlineFontSizeUnit,
      ...defaultExtraValues,
      pageWidth:
        selectedCourse?.globalStyle?.pageWidth ?? globalStyleDefault.pageWidth,
      pageWidthUnit:
        selectedCourse?.globalStyle?.pageWidthUnit ??
        globalStyleDefault.pageWidthUnit,
      paragraphFont: selectedCourse?.globalStyle?.paragraphFont?.family ?? '',
      paragraphFontColor:
        selectedCourse?.globalStyle?.paragraphFontColor ??
        globalStyleDefault.paragraphFontColor,
      paragraphFontSize:
        selectedCourse?.globalStyle?.paragraphFontSize ??
        globalStyleDefault.paragraphFontSize,
      paragraphFontSizeUnit:
        selectedCourse?.globalStyle?.paragraphFontSizeUnit ??
        globalStyleDefault.paragraphFontSizeUnit,
      primaryColor:
        selectedCourse?.globalStyle?.primaryColor ??
        globalStyleDefault.primaryColor,
      secondaryColor:
        selectedCourse?.globalStyle?.secondaryColor ??
        globalStyleDefault.secondaryColor,
      templateColors: generateOldNewTemplateColors(
        selectedCourse?.globalStyle?.templateColors ??
          globalStyleDefault.templateColors
      ),
      additionalColors: generateOldNewAdditionalColors(
        selectedCourse?.globalStyle?.additionalColors?.at(0) ??
          globalStyleDefault.additionalColors?.at(0) ??
          {}
      ),
    },
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
    trigger,
    control,
  } = globalStyleSchemaModalForm;

  const values = watch();
  const { addToast } = useToast();
  const [loadingModal, setLoadingModal] = useState(true);
  const [fonts, setFonts] = useState<IFont[]>([]);
  const loadedFontsOnce = useRef(false);

  const resetForm = useCallback(() => {
    if (isHueRotateRemoved) {
      reset();
      return;
    }
    const globalStyle = selectedCourse?.globalStyle ?? globalStyleDefault;
    const defaultFont = fonts.at(0) ?? globalStyleDefault.paragraphFont;

    reset({
      buttonColor: globalStyle?.buttonColor ?? globalStyleDefault.buttonColor,
      buttonFontColor:
        globalStyle?.buttonFontColor ?? globalStyleDefault.buttonFontColor,
      headlineFont:
        globalStyle?.headlineFont?.family ?? defaultFont?.family ?? '',
      headlineFontColor:
        globalStyle?.headlineFontColor ?? globalStyleDefault.headlineFontColor,
      headlineFontSize:
        globalStyle?.headlineFontSize ?? globalStyleDefault.headlineFontSize,
      headlineFontSizeUnit:
        globalStyle?.headlineFontSizeUnit ??
        globalStyleDefault.headlineFontSizeUnit,
      ...defaultExtraValues,
      pageWidth: globalStyle?.pageWidth ?? globalStyleDefault.pageWidth,
      pageWidthUnit:
        globalStyle?.pageWidthUnit ?? globalStyleDefault.pageWidthUnit,
      paragraphFont:
        globalStyle?.paragraphFont?.family ?? defaultFont?.family ?? '',
      paragraphFontColor:
        globalStyle?.paragraphFontColor ??
        globalStyleDefault.paragraphFontColor,
      paragraphFontSize:
        globalStyle?.paragraphFontSize ?? globalStyleDefault.paragraphFontSize,
      paragraphFontSizeUnit:
        globalStyle?.paragraphFontSizeUnit ??
        globalStyleDefault.paragraphFontSizeUnit,
      primaryColor:
        globalStyle?.primaryColor ?? globalStyleDefault.primaryColor,
      secondaryColor:
        globalStyle?.secondaryColor ?? globalStyleDefault.secondaryColor,
      templateColors: generateOldNewTemplateColors(
        selectedCourse?.globalStyle?.templateColors ??
          globalStyleDefault.templateColors
      ),
      additionalColors: generateOldNewAdditionalColors(
        globalStyle?.additionalColors?.at(0) ??
          globalStyleDefault.additionalColors?.at(0) ??
          {}
      ),
    });
  }, [
    defaultExtraValues,
    fonts,
    isHueRotateRemoved,
    reset,
    selectedCourse?.globalStyle,
  ]);

  const onSubmit: SubmitHandler<GlobalStyleSchema> = ({
    buttonColor,
    buttonFontColor,
    headlineFontColor,
    headlineFontSize,
    headlineFontSizeUnit,
    pageWidth,
    pageWidthUnit,
    paragraphFontColor,
    paragraphFontSize,
    paragraphFontSizeUnit,
    primaryColor,
    secondaryColor,
    templateColors,
    additionalColors,
    headlineFont: headlineFontFamilyName,
    paragraphFont: paragraphFontFamilyName,
    ...extraData
  }: GlobalStyleSchema) => {
    const globalStyle = selectedCourse?.globalStyle ?? globalStyleDefault;
    const defaultFont = fonts.at(0) ?? globalStyleDefault.paragraphFont;

    let headlineFont = globalStyle.headlineFont ?? defaultFont;
    let paragraphFont = globalStyle.paragraphFont ?? defaultFont;

    for (const font of fonts) {
      if (font.family === headlineFontFamilyName) {
        headlineFont = font;
      }
      if (font.family === paragraphFontFamilyName) {
        paragraphFont = font;
      }
    }

    void onAccept?.({
      buttonColor,
      buttonFontColor,
      headlineFont,
      headlineFontColor,
      headlineFontSize,
      headlineFontSizeUnit,
      pageWidth,
      pageWidthUnit,
      paragraphFont,
      paragraphFontColor,
      paragraphFontSize,
      paragraphFontSizeUnit,
      primaryColor,
      secondaryColor,
      templateColors: generateTemplateColorsToUpdate(templateColors),
      additionalColors: generateAdditionalColorsToUpdate(
        globalStyle.additionalColors?.at(0) ??
          globalStyleDefault.additionalColors?.at(0) ??
          {},
        additionalColors
      ),
      ...extraData,
    });
  };

  const additionalColorsBottomRef = useRef<HTMLDivElement>(null);

  const hasAnyTemplateColor = useMemo(
    () =>
      [
        values.templateColors.primaryColor,
        values.templateColors.secondaryColor,
        values.templateColors.backgroundColor,
        values.templateColors.accentColor,
      ].some((value) => value),
    [values.templateColors]
  );
  const { fields } = useFieldArray({
    control,
    keyName: 'key',
    name: 'additionalColors',
  });

  const handleDeclineSaveGlobalStyle = useCallback(() => {
    if (!isSelectOpen.current.size) {
      loadedFontsOnce.current = false;
      onDecline?.();
      resetForm();
    }
  }, [onDecline, resetForm]);

  const handleHeadlineFontSelectChange = useCallback(
    (value: string) => {
      setValue('headlineFont', value);
    },
    [setValue]
  );

  const handleParagraphFontSelectChange = useCallback(
    (value: string) => {
      setValue('paragraphFont', value);
    },
    [setValue]
  );

  const handlePageWidthChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { valueAsNumber } = target;

    switch (true) {
      case values.pageWidthUnit === '%' &&
        valueAsNumber > maxValuePercentagePageWidth:
        setValue('pageWidth', maxValuePercentagePageWidth);
        break;
      case valueAsNumber < minValuePageWidth:
        setValue('pageWidth', minValuePageWidth);
        break;
      case valueAsNumber > maxValuePageWidth:
        setValue('pageWidth', maxValuePageWidth);
        break;
    }
  };

  const handleHeadlineFontSizeChange = ({
    target: { valueAsNumber },
  }: ChangeEvent<HTMLInputElement>) => {
    switch (true) {
      case valueAsNumber < minValueFontSize:
        setValue('headlineFontSize', minValueFontSize);
        break;
      case valueAsNumber > maxValueFontSize:
        setValue('headlineFontSize', maxValueFontSize);
        break;
    }
  };

  const handleParagraphFontSizeChange = useCallback(
    ({ target: { valueAsNumber } }: ChangeEvent<HTMLInputElement>) => {
      switch (true) {
        case valueAsNumber < minValueFontSize:
          setValue('paragraphFontSize', minValueFontSize);
          break;
        case valueAsNumber > maxValueFontSize:
          setValue('paragraphFontSize', maxValueFontSize);
          break;
      }
    },
    [setValue]
  );

  const handlePageWidthUnitChange = useCallback(
    (value: string) => {
      setValue('pageWidth', minValuePageWidth);
      setValue('pageWidthUnit', value);
    },
    [setValue]
  );

  const handleHeadlineFontUnitChange = useCallback(
    (value: string) => {
      setValue('headlineFontSizeUnit', value);
    },
    [setValue]
  );

  const handleParagraphFontUnitChange = useCallback(
    (value: string) => {
      setValue('paragraphFontSizeUnit', value);
    },
    [setValue]
  );

  const handleHeadlineFontColorChange = useCallback(
    (color: string) => {
      setValue('headlineFontColor', color);
      void trigger('headlineFontColor');
    },
    [setValue, trigger]
  );

  const handleParagraphFontColorChange = useCallback(
    (color: string) => {
      setValue('paragraphFontColor', color);
      void trigger('paragraphFontColor');
    },
    [setValue, trigger]
  );

  const handleButtonBackgroundColorChange = useCallback(
    (color: string) => {
      setValue('buttonColor', color);
      void trigger('buttonColor');
    },
    [setValue, trigger]
  );

  const handleButtonFontColorChange = useCallback(
    (color: string) => {
      setValue('buttonFontColor', color);
      void trigger('buttonFontColor');
    },
    [setValue, trigger]
  );

  const handlePrimaryColorChange = useCallback(
    (color: string) => {
      setValue('primaryColor', color);
      void trigger('primaryColor');
    },
    [setValue, trigger]
  );

  const handleSecondaryColorChange = useCallback(
    (color: string) => {
      setValue('secondaryColor', color);
      void trigger('secondaryColor');
    },
    [setValue, trigger]
  );

  useEffect(() => {
    loadedFontsOnce.current = false;
  }, [courseId]);

  useEffect(() => {
    const loadFontsOnce = () => {
      loadedFontsOnce.current = true;
      setLoadingModal(true);

      const resultWithIndex: IFont[] =
        selectedCourse?.fonts.map((data, index) => ({
          ...data,
          index,
          origin: 'course',
        })) ?? [];
      const grapesFontsWithIndex: IFont[] = fontsGrapes.map((data, index) => ({
        ...data,
        index,
      }));

      let currentIndex = 0;
      const allFontsWithIndex = [
        ...grapesFontsWithIndex.map((obj) => ({
          ...obj,
          index: currentIndex++,
        })),
        ...resultWithIndex.map((obj) => ({
          ...obj,
          index: currentIndex++,
        })),
      ];
      setFonts(allFontsWithIndex);

      setLoadingModal(false);

      return allFontsWithIndex satisfies IFont[];
    };

    let defaultFont = fonts.at(0);

    if (open && courseId && !loadedFontsOnce.current) {
      const newFonts = loadFontsOnce();
      defaultFont = newFonts.at(0) ?? defaultFont;
    }

    if (!values.headlineFont) {
      setValue('headlineFont', defaultFont?.family ?? '');
    }

    if (!values.paragraphFont) {
      setValue('paragraphFont', defaultFont?.family ?? '');
    }
  }, [
    addToast,
    courseId,
    fonts,
    open,
    handleParagraphFontSelectChange,
    selectedCourse,
    setValue,
    values.headlineFont,
    values.paragraphFont,
  ]);

  const forceUpdateGlobalStyle = useCallback(
    (globalStyle: IGlobalStyleDTO) => {
      if (!selectedCourse?.globalStyle) return;
      updateGlobalStyle(globalStyle);
      reset({
        ...values,
        headlineFont: globalStyle.headlineFont?.family ?? '',
        paragraphFont: globalStyle.paragraphFont?.family ?? '',
        templateColors: generateOldNewTemplateColors(
          globalStyle.templateColors ?? globalStyleDefault.templateColors
        ),
        additionalColors: generateOldNewAdditionalColors(
          globalStyle?.additionalColors?.at(0) ??
            globalStyleDefault.additionalColors?.at(0) ??
            {}
        ),
      });
    },
    [selectedCourse?.globalStyle?.id, reset, values, updateGlobalStyle]
  );

  useEffect(() => {
    if (selectedCourse?.globalStyle && open) {
      void getGlobalStyleById({
        id: selectedCourse?.globalStyle?.id ?? 0,
      }).then(forceUpdateGlobalStyle);
    }
  }, [open]);

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectOpen.current.add(key);
    } else {
      isSelectOpen.current.delete(key);
    }
  }, []);

  const fontsData = useMemo(
    () => fonts.map((font) => ({ key: font.family, value: font.family })),
    [fonts]
  );

  const handleTemplateColorsChange = useCallback(
    (
      color: string,
      colorKey?:
        | `additionalColors.${number}.color`
        | `templateColors.accentColor`
        | `templateColors.backgroundColor`
        | `templateColors.primaryColor`
        | `templateColors.secondaryColor`
    ) => {
      if (colorKey) {
        setValue(colorKey, color.toLowerCase());
        void trigger(colorKey);
      }
    },
    [setValue, trigger]
  );

  const handleTemplatePrimaryColorChange = useCallback(
    (color: string) => {
      if (!color) {
        handleTemplateColorsChange('#', 'templateColors.primaryColor');
        return;
      }
      handleTemplateColorsChange(color, 'templateColors.primaryColor');
    },
    [handleTemplateColorsChange]
  );
  const handleTemplateSecondaryColorChange = useCallback(
    (color: string) => {
      if (!color) {
        handleTemplateColorsChange('#', 'templateColors.secondaryColor');
        return;
      }
      handleTemplateColorsChange(color, 'templateColors.secondaryColor');
    },
    [handleTemplateColorsChange]
  );
  const handleTemplateBackgroundColorChange = useCallback(
    (color: string) => {
      if (!color) {
        handleTemplateColorsChange('#', 'templateColors.backgroundColor');
        return;
      }
      handleTemplateColorsChange(color, 'templateColors.backgroundColor');
    },
    [handleTemplateColorsChange]
  );
  const handleTemplateAccentColorChange = useCallback(
    (color: string) => {
      if (!color) {
        handleTemplateColorsChange('#', 'templateColors.accentColor');
        return;
      }
      handleTemplateColorsChange(color, 'templateColors.accentColor');
    },
    [handleTemplateColorsChange]
  );

  const handleOpenSwapColors = useCallback(() => {
    setIsSwapColorsOpen(true);
  }, []);

  const handleCloseSwapColors = useCallback(() => {
    setIsSwapColorsOpen(false);
  }, []);

  const handleSwapColorsAccept = useCallback(
    async (data: TemplateColorsSchema) => {
      try {
        const baseStyle = selectedCourse?.globalStyle;
        if (!baseStyle?.id) return;
        setIsLoadingModalSwapColors(true);
        await swapColors({
          id: baseStyle.id,
          templateColors: generateTemplateColorsToUpdate(data.templateColors),
          additionalColors: generateAdditionalColorsToUpdate(
            baseStyle.additionalColors?.at(0) ?? {},
            data.additionalColors
          ),
        })
          .then((updatedGlobalStyle) => {
            if (selectedCourse) {
              forceUpdateGlobalStyle(updatedGlobalStyle);
              setIsSwapColorsOpen(false);
            }
          })
          .catch((err: IApiThrowsError) => {
            Logger.error('Error updating colors:', err);
            addToast({
              title: 'Error update Template Colors',
              description:
                err.response && err.response.status < 500
                  ? err.response.data.message
                  : DefaultDescriptionBenefitEducation,
              styleType: 'error',
              dataCy: 'error-toast',
            });
          });
      } finally {
        setIsLoadingModalSwapColors(false);
      }
    },
    [selectedCourse, forceUpdateGlobalStyle]
  );

  return !open ? null : (
    <>
      <FormBase.Provider {...globalStyleSchemaModalForm}>
        <FormModalCustom
          acceptText="Save"
          dataCy="global-styles-dialog-modal"
          declineText="Cancel"
          mainText="Global Styles"
          disabled={!isValid || isSwapColorsOpen}
          {...props}
          onAccept={handleSubmit(onSubmit)}
          loading={loading}
          onDecline={() => {
            handleDeclineSaveGlobalStyle();
          }}
          onOpenChange={handleDeclineSaveGlobalStyle}
          open={!waitToOpen}
          acceptButtonTooltipText={
            needSave
              ? 'Please save your Course before updating the Global Styles.'
              : null
          }
        >
          {loadingModal ? (
            <LoadingContainer data-cy="loading-fonts-container">
              <Loading dataCy="loading-fonts" />
            </LoadingContainer>
          ) : (
            <Container>
              <>
                <InputContentCustom
                  dataCy="page-width-input"
                  errorMessage={
                    errors.pageWidth?.message ?? errors.pageWidthUnit?.message
                  }
                  filled={
                    Boolean(values?.pageWidth) || Boolean(values?.pageWidthUnit)
                  }
                  inputRef="page-width-input"
                  label="Page Width"
                >
                  <SmallInputTextCustom
                    dataCy="page-width-input"
                    id="page-width-input"
                    name="pageWidth"
                    max={maxValuePageWidth}
                    min={minValuePageWidth}
                    onKeyDown={(event) => {
                      if (!NumberKeyRegex.test(event.key))
                        event.preventDefault();
                    }}
                    onChange={handlePageWidthChange}
                    required
                    type="number"
                  />
                  <SmallSelectDataCustom
                    data={fontUnitsData}
                    dataCy="select-page-width-unit"
                    defaultValue={
                      values.pageWidthUnit ?? fontUnitsData.at(0)?.key
                    }
                    name="select-page-width-unit"
                    onValueChange={handlePageWidthUnitChange}
                    onOpenChange={(isOpen) => {
                      handleOpenChange(isOpen, 'width');
                    }}
                    zIndex={9999}
                  />
                </InputContentCustom>
                <InputContentCustom
                  dataCy="select-headline-font"
                  errorMessage={errors.headlineFont?.message}
                  filled={Boolean(values.headlineFont)}
                  inputRef="select-headline-font"
                  label="Headline Font"
                >
                  <SelectDataCustom
                    data={fontsData}
                    dataCy="select-headline-font"
                    defaultValue={values.headlineFont ?? fonts.at(0)?.family}
                    name="select-headline-font"
                    onValueChange={handleHeadlineFontSelectChange}
                    onOpenChange={(isOpen) => {
                      handleOpenChange(isOpen, 'headlineFont');
                    }}
                    zIndex={9999}
                  />
                </InputContentCustom>
                <InputContentCustom
                  dataCy="headline-font-size"
                  errorMessage={
                    errors.headlineFontSize?.message ??
                    errors.headlineFontSizeUnit?.message
                  }
                  filled={
                    Boolean(values.headlineFontSize) ||
                    Boolean(values.headlineFontSizeUnit)
                  }
                  inputRef="headline-font-size"
                  label="Headline Font Size"
                >
                  <SmallInputTextCustom
                    dataCy="headline-font-size"
                    id="headline-font-size"
                    max={maxValueFontSize}
                    min={minValueFontSize}
                    name="headlineFontSize"
                    onKeyDown={(event) => {
                      if (!NumberKeyRegex.test(event.key))
                        event.preventDefault();
                    }}
                    onChange={handleHeadlineFontSizeChange}
                    required
                    type="number"
                  />
                  <SmallSelectDataCustom
                    data={fontUnitsData}
                    dataCy="select-headline-font-unit"
                    defaultValue={
                      values.headlineFontSizeUnit ?? fontUnitsData.at(0)?.key
                    }
                    name="select-headline-font-unit"
                    onValueChange={handleHeadlineFontUnitChange}
                    onOpenChange={(isOpen) => {
                      handleOpenChange(isOpen, 'headlineFontUnit');
                    }}
                    zIndex={9999}
                  />
                </InputContentCustom>
                <ColorPickerInput
                  color={values.headlineFontColor ?? ''}
                  dataCy="headline-font-color"
                  errorMessage={errors.headlineFontColor?.message}
                  label="Headline color"
                  name="headlineFontColor"
                  onChange={handleHeadlineFontColorChange}
                />
                <InputContentCustom
                  dataCy="select-paragraph-font"
                  errorMessage={errors.paragraphFont?.message}
                  filled={Boolean(values.paragraphFont)}
                  inputRef="select-paragraph-font"
                  label="Text Font"
                >
                  <SelectDataCustom
                    data={fontsData}
                    dataCy="select-paragraph-font"
                    defaultValue={values.paragraphFont ?? fonts.at(0)?.family}
                    name="select-paragraph-font"
                    onValueChange={handleParagraphFontSelectChange}
                    onOpenChange={(isOpen) => {
                      handleOpenChange(isOpen, 'paragraphFont');
                    }}
                    zIndex={9999}
                  />
                </InputContentCustom>
                <InputContentCustom
                  dataCy="paragraph-font-size"
                  errorMessage={
                    errors.paragraphFontSize?.message ??
                    errors.paragraphFontSizeUnit?.message
                  }
                  filled={
                    Boolean(values.paragraphFontSize) ||
                    Boolean(values.paragraphFontSizeUnit)
                  }
                  inputRef="paragraph-font-size"
                  label="Text Font Size"
                >
                  <SmallInputTextCustom
                    dataCy="paragraph-font-size"
                    id="paragraph-font-size"
                    name="paragraphFontSize"
                    max={maxValueFontSize}
                    min={minValueFontSize}
                    onKeyDown={(event) => {
                      if (!NumberKeyRegex.test(event.key))
                        event.preventDefault();
                    }}
                    onChange={handleParagraphFontSizeChange}
                    required
                    type="number"
                  />
                  <SmallSelectDataCustom
                    data={fontUnitsData}
                    dataCy="select-paragraph-font-unit"
                    defaultValue={
                      values.paragraphFontSizeUnit ?? fontUnitsData.at(0)?.key
                    }
                    name="select-paragraph-font-unit"
                    onValueChange={handleParagraphFontUnitChange}
                    onOpenChange={(isOpen) => {
                      handleOpenChange(isOpen, 'paragraphFontUnit');
                    }}
                    zIndex={9999}
                  />
                </InputContentCustom>
                <ColorPickerInput
                  color={values.paragraphFontColor ?? ''}
                  dataCy="paragraph-color"
                  errorMessage={errors.paragraphFontColor?.message}
                  label="Text color"
                  name="paragraphFontColor"
                  onChange={handleParagraphFontColorChange}
                />
                <ColorPickerInput
                  color={values.buttonColor ?? ''}
                  dataCy="background-button-color"
                  errorMessage={errors.buttonColor?.message}
                  label="Background button color"
                  name="buttonColor"
                  onChange={handleButtonBackgroundColorChange}
                />
                <ColorPickerInput
                  color={values.buttonFontColor ?? ''}
                  dataCy="button-color"
                  errorMessage={errors.buttonFontColor?.message}
                  label="Button font color"
                  name="buttonFontColor"
                  onChange={handleButtonFontColorChange}
                />
                <ColorPickerInput
                  color={values.primaryColor ?? ''}
                  dataCy="primary-color"
                  errorMessage={errors.primaryColor?.message}
                  label="Primary color"
                  name="primaryColor"
                  onChange={handlePrimaryColorChange}
                />
                <ColorPickerInput
                  color={values.secondaryColor ?? ''}
                  dataCy="secondary-color"
                  errorMessage={errors.secondaryColor?.message}
                  label="Secondary color"
                  name="secondaryColor"
                  onChange={handleSecondaryColorChange}
                />
                {extraFields}
                <TemplateImagesColorsSection hasColors={hasAnyTemplateColor}>
                  <TemplateImagesColorsContainer>
                    <TemplateImagesColorsTitle>
                      Template Images Colors
                    </TemplateImagesColorsTitle>
                    <IconButton
                      type="button"
                      data-cy="add-template-color-button"
                      onClick={handleOpenSwapColors}
                    >
                      <HiOutlineColorSwatch size={28} />
                    </IconButton>
                  </TemplateImagesColorsContainer>
                  {hasAnyTemplateColor && (
                    <>
                      {values.templateColors.primaryColor !== undefined && (
                        <ColorPickerInput
                          color={values.templateColors.primaryColor}
                          dataCy={`template-color-${values.templateColors.primaryColor}`}
                          errorMessage={
                            errors.templateColors?.primaryColor?.message
                          }
                          label="Primary Color"
                          name="templateColors.primaryColor"
                          onChange={handleTemplatePrimaryColorChange}
                          required={false}
                        />
                      )}
                      {values.templateColors.secondaryColor !== undefined && (
                        <ColorPickerInput
                          color={values.templateColors.secondaryColor}
                          dataCy={`template-color-${values.templateColors.secondaryColor}`}
                          errorMessage={
                            errors.templateColors?.secondaryColor?.message
                          }
                          label="Secondary Color"
                          name="templateColors.secondaryColor"
                          onChange={handleTemplateSecondaryColorChange}
                          required={false}
                        />
                      )}
                      {values.templateColors.backgroundColor !== undefined && (
                        <ColorPickerInput
                          color={values.templateColors.backgroundColor}
                          dataCy={`template-color-${values.templateColors.backgroundColor}`}
                          errorMessage={
                            errors.templateColors?.backgroundColor?.message
                          }
                          label="Background Color"
                          name="templateColors.backgroundColor"
                          onChange={handleTemplateBackgroundColorChange}
                          required={false}
                        />
                      )}
                      {values.templateColors.accentColor !== undefined && (
                        <ColorPickerInput
                          color={values.templateColors.accentColor}
                          dataCy={`template-color-${values.templateColors.accentColor}`}
                          errorMessage={
                            errors.templateColors?.accentColor?.message
                          }
                          label="Accent Color"
                          name="templateColors.accentColor"
                          onChange={handleTemplateAccentColorChange}
                          required={false}
                        />
                      )}
                    </>
                  )}
                </TemplateImagesColorsSection>

                <TemplateImagesColorsSection hasColors={hasAnyTemplateColor}>
                  {hasAnyTemplateColor && (
                    <BaseCollapsibleMenu
                      baseItemText="Additional Colors"
                      scrollToBottomFunction={() => {
                        additionalColorsBottomRef?.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }}
                    >
                      {fields.map(({ key }, idx) => {
                        return (
                          <ColorPickerContainer key={key}>
                            <ColorPickerSelectData
                              color={
                                values?.additionalColors?.[idx]?.color ?? ''
                              }
                              dataCy={`template-color-${key}`}
                              errorMessage={
                                errors?.additionalColors?.at?.(idx)?.color
                                  ?.message
                              }
                              name={`additionalColors.${idx}.color`}
                              label="Additional Color"
                              onChange={(color, _colorKey) => {
                                if (color) {
                                  handleTemplateColorsChange(
                                    color,
                                    `additionalColors.${idx}.color`
                                  );
                                }
                              }}
                              onOpenChange={(isOpen) => {
                                handleOpenChange(isOpen, key);
                              }}
                            />
                          </ColorPickerContainer>
                        );
                      })}
                    </BaseCollapsibleMenu>
                  )}
                  <div ref={additionalColorsBottomRef}></div>
                  {!hasAnyTemplateColor && (
                    <>
                      <ColorPickerInput
                        dataCy="no-template-primary-color"
                        disabled
                        name="noTemplatePrimaryColor"
                        color={globalStyleDefault.templateColors.primaryColor}
                        required={false}
                      />
                      <ColorPickerInput
                        dataCy="no-template-secondary-color"
                        disabled
                        name="noTemplateSecondaryColor"
                        color={globalStyleDefault.templateColors.secondaryColor}
                        required={false}
                      />
                      <ColorPickerInput
                        dataCy="no-template-background-color"
                        disabled
                        name="noTemplateBackgroundColor"
                        color={
                          globalStyleDefault.templateColors.backgroundColor
                        }
                        required={false}
                      />
                      <ColorPickerInput
                        dataCy="no-template-accent-color"
                        disabled
                        name="noTemplateAccentColor"
                        color={globalStyleDefault.templateColors.accentColor}
                        required={false}
                      />
                      <OverlayDiv data-cy="no-template-colors-overlay">
                        <OverlayText data-cy="no-template-colors-overlay-text">
                          You&apos;d need an SVG to get the templates color
                        </OverlayText>
                      </OverlayDiv>
                    </>
                  )}
                </TemplateImagesColorsSection>
              </>
            </Container>
          )}
        </FormModalCustom>
      </FormBase.Provider>

      <ModalSwapColors
        courseId={courseId}
        loading={isLoadingModalSwapColors}
        onAccept={handleSwapColorsAccept}
        onDecline={handleCloseSwapColors}
        open={isSwapColorsOpen}
      />
    </>
  );
};

interface IGlobalStyleModalProps
  extends Omit<
    IGlobalStyleModalBaseProps<GlobalStyleSchema>,
    'defaultExtraValues' | 'extraFields' | 'schema'
  > {}

export const GlobalStyleModal = ({
  ...props
}: IGlobalStyleModalProps): JSX.Element => {
  const { selectedCourse } = useCourse();

  return (
    <FeatureFlag
      api={adminApi}
      flagKey={FeatureFlagKey.REMOVE_HUEROTATE}
      fallback={
        <GlobalStyleModalBase
          {...props}
          extraFields={<HueRotateField />}
          defaultExtraValues={{
            hueRotate:
              selectedCourse?.globalStyle?.hueRotate ??
              globalStyleDefault.hueRotate,
          }}
          schema={globalStyleWithHueSchema}
        />
      }
      loadingComponent={
        <LoadingContainer data-cy="loading-fonts-container">
          <Loading dataCy="loading-fonts" />
        </LoadingContainer>
      }
    >
      <GlobalStyleModalBase {...props} />
    </FeatureFlag>
  );
};

const HueRotateField = () => {
  const {
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<GlobalStyleSchemaWithHue>();

  const [hueRotateBaseColor, setHueRotateBaseColor] = useState(
    globalStyleDefault.secondaryColor
  );

  const values = watch();

  const hueRotateSlider = useMemo(
    () => [values.hueRotate ?? 0],
    [values.hueRotate ?? 0]
  );

  const handleHueRotateBaseColorChange = useCallback((color: string) => {
    setHueRotateBaseColor(color);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (!NumberKeyRegex.test(event.key)) event.preventDefault();
    },
    []
  );

  const handleHueRotateChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { valueAsNumber } = target;

      switch (true) {
        case valueAsNumber < minHueRotate:
          setValue('hueRotate', minHueRotate);
          break;
        case valueAsNumber > maxHueRotate:
          setValue('hueRotate', maxHueRotate);
          break;
      }
    },
    [setValue]
  );

  const handleHueRotateSliderChange = useCallback(
    (value: number[]) => {
      const newValue = value.at(0);
      setValue('hueRotate', newValue ?? globalStyleDefault.hueRotate);
    },
    [setValue]
  );

  return (
    <ColorPickerInput
      color={hueRotateBaseColor}
      dataCy="hue-rotate"
      errorMessage={errors.hueRotate?.message}
      label={'Template Images Tint'}
      name="hueRotateBaseColor"
      onChange={handleHueRotateBaseColorChange}
      customPreview={
        <ColorPreview
          style={{
            backgroundColor: hueRotateBaseColor,
            filter: `hue-rotate(${values.hueRotate ?? 0}deg)`,
          }}
          title="Preview Base Color with Hue Rotate"
        />
      }
      customInput={(_handleColorChange) => (
        <>
          <SmallInputTextCustom
            dataCy="hue-input"
            id="hue-rotate"
            name="hueRotate"
            max={maxHueRotate}
            min={minHueRotate}
            onKeyDown={handleKeyDown}
            onChange={handleHueRotateChange}
            required
            type="number"
          />
          <SliderCustom
            max={maxHueRotate}
            min={minHueRotate}
            onValueChange={handleHueRotateSliderChange}
            value={hueRotateSlider}
          />
        </>
      )}
    />
  );
};
